/**
 * `@platform/api-client` - the one way a browser talks to the platform API
 * (MASTER_PLAN M2.10, API.md §1, ADR-004, ADR-007).
 *
 * ## What it is
 *
 * A thin wrapper over `fetch`. No runtime dependency, no request cache, no retry
 * policy, no schema validation: those belong to TanStack Query, to the server and to
 * `packages/contracts` respectively. What lives here is exactly the handful of things
 * every call must get right and no call site should have to remember:
 *
 * - **`credentials: 'include'`** on every request. The session cookie is HttpOnly
 *   (`__Host-ha_session`, or `ha_session` on a plain-http dev machine), so the SPA can
 *   neither read nor set it; omitting this is the single most common way to build a
 *   client that appears to sign in and then finds itself anonymous (SECURITY.md §4.4).
 * - **No CSRF token.** The API checks the browser's own `Origin` header (API.md §1.4).
 *   There is nothing to thread through, and a mismatch is `403 CSRF_ORIGIN_MISMATCH` -
 *   in development that means the Vite origin has to be in `CORS_ALLOWED_ORIGINS`.
 * - **The error envelope**, parsed into `ApiError` so a call site can show
 *   `error.message` and branch on `error.code` (see `errors.ts`).
 * - **`onUnauthenticated` / `onStepUpRequired`**, so the app reacts to a session that
 *   ended, or to a second factor that is due, in one place instead of in every screen.
 *
 * ## Why the callbacks and not an interceptor stack
 *
 * There are exactly two cross-cutting reactions in this product - "you are signed out"
 * and "verify a second factor" - and both are the *app's* decision (which route to send
 * the patient to, which dialog to open). Two named callbacks say that plainly; a
 * general middleware chain would invite request-mutating logic to accumulate in a layer
 * no screen can see.
 */

import { createAuthApi, type AuthApi } from './auth.js';
import { ApiError, errorFromBody, parseRetryAfter } from './errors.js';
import type { BodyOf, HttpMethod, paths, PathsWith, ResponseOf } from './paths.js';

export type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

/** Query-string values the client knows how to serialize. `undefined` is dropped. */
export type QueryValue = string | number | boolean | undefined | null;

export interface RequestOptions {
  query?: Record<string, QueryValue>;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** Sent as `Idempotency-Key` (API.md §1.8). */
  idempotencyKey?: string;
}

export interface ApiClientOptions {
  /**
   * Origin the API is served from. Empty (the default) means "this page's origin",
   * which is what both production and the Vite dev proxy want; paths already carry the
   * `/api/v1` prefix, because that is how the OpenAPI document spells them.
   */
  baseUrl?: string;
  /** Injected in tests; defaults to the global `fetch`. */
  fetch?: FetchLike;
  /** Called for every `401`, before the error is thrown. */
  onUnauthenticated?: (error: ApiError) => void;
  /** Called for every `403 MFA_STEP_UP_REQUIRED`, before the error is thrown. */
  onStepUpRequired?: (error: ApiError) => void;
}

export interface ApiClient {
  readonly baseUrl: string;

  /** Every auth flow the patient app offers (ADR-004). */
  readonly auth: AuthApi;

  /**
   * The one call every SPA makes at boot (API.md §2.2). Always `200`: an anonymous
   * visitor gets `authenticated: false`, not a `401`, so a public booking page does not
   * have to treat an expected state as an error.
   */
  getSession(options?: RequestOptions): Promise<SessionSummary>;

  /** Branding, legal links and booking configuration for the resolved host. */
  getPublicTenant(options?: RequestOptions): Promise<PublicTenant>;

  get<Path extends PathsWith<'get'>>(
    path: Path,
    options?: RequestOptions,
  ): Promise<ResponseOf<Path, 'get'>>;

  post<Path extends PathsWith<'post'>>(
    path: Path,
    body?: BodyOf<Path, 'post'>,
    options?: RequestOptions,
  ): Promise<ResponseOf<Path, 'post'>>;

  /**
   * A whole-resource replace. The API uses it where a payload is the complete new state
   * rather than a set of edits - `PUT /patient/notification-preferences` is the first -
   * and the distinction matters to the caller, so it gets its own helper instead of
   * being folded into `patch`.
   */
  put<Path extends PathsWith<'put'>>(
    path: Path,
    body?: BodyOf<Path, 'put'>,
    options?: RequestOptions,
  ): Promise<ResponseOf<Path, 'put'>>;

  patch<Path extends PathsWith<'patch'>>(
    path: Path,
    body?: BodyOf<Path, 'patch'>,
    options?: RequestOptions,
  ): Promise<ResponseOf<Path, 'patch'>>;

  delete<Path extends PathsWith<'delete'>>(
    path: Path,
    options?: RequestOptions,
  ): Promise<ResponseOf<Path, 'delete'>>;

  /**
   * The escape hatch, and the only way to reach the Better Auth mount - its routes are
   * a Fastify wildcard and therefore absent from the OpenAPI document. `auth.ts` is the
   * only place in the platform that should need it.
   */
  request<Result>(
    method: HttpMethod,
    path: string,
    body?: unknown,
    options?: RequestOptions,
  ): Promise<Result>;
}

/** The session summary as the API declares it - `GET /api/v1/auth/session`. */
export type SessionSummary = ResponseOf<'/api/v1/auth/session', 'get'>['data'];

/** The public tenant payload - `GET /api/v1/public/tenant`. */
export type PublicTenant = ResponseOf<'/api/v1/public/tenant', 'get'>['data'];

/** Every path the document declares, for call sites that want to name one. */
export type ApiPath = keyof paths;

export function createApiClient(options: ApiClientOptions = {}): ApiClient {
  const baseUrl = (options.baseUrl ?? '').replace(/\/$/, '');
  const doFetch: FetchLike = options.fetch ?? ((input, init) => globalThis.fetch(input, init));

  async function request<Result>(
    method: HttpMethod,
    path: string,
    body?: unknown,
    request_options: RequestOptions = {},
  ): Promise<Result> {
    const url = baseUrl + path + queryString(request_options.query);

    const headers: Record<string, string> = {
      accept: 'application/json',
      ...request_options.headers,
    };
    if (body !== undefined) headers['content-type'] = 'application/json';
    if (request_options.idempotencyKey !== undefined) {
      headers['idempotency-key'] = request_options.idempotencyKey;
    }

    const init: RequestInit = {
      method: method.toUpperCase(),
      headers,
      // The whole point of this package (SECURITY.md §4.4).
      credentials: 'include',
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      ...(request_options.signal === undefined ? {} : { signal: request_options.signal }),
    };

    let response: Response;
    try {
      response = await doFetch(url, init);
    } catch (cause) {
      // An aborted request is the caller's own doing (a component unmounted, a newer
      // keystroke superseded this one) and must stay an AbortError so TanStack Query
      // and `AbortSignal.timeout` can tell it apart from a failure. React Native has no
      // `DOMException` global (M18.3), and `instanceof` an undefined identifier throws a
      // ReferenceError that would mask the real failure - hence the `typeof` guard.
      if (
        typeof DOMException !== 'undefined' &&
        cause instanceof DOMException &&
        cause.name === 'AbortError'
      ) {
        throw cause;
      }
      throw new ApiError({
        status: 0,
        code: 'NETWORK_ERROR',
        message: 'We could not reach the server. Check your connection and try again.',
        correlationId: null,
      });
    }

    if (!response.ok) {
      const error = errorFromBody(
        response.status,
        await readJson(response),
        parseRetryAfter(response.headers.get('retry-after')),
      );
      if (error.isUnauthenticated) options.onUnauthenticated?.(error);
      if (error.isStepUpRequired) options.onStepUpRequired?.(error);
      throw error;
    }

    if (response.status === 204) return undefined as Result;
    const parsed = await readJson(response);
    return parsed as Result;
  }

  return {
    baseUrl,
    request,
    auth: createAuthApi(request),
    getSession: async (request_options) => {
      const body = await request<{ data: SessionSummary }>(
        'get',
        '/api/v1/auth/session',
        undefined,
        request_options,
      );
      return body.data;
    },
    getPublicTenant: async (request_options) => {
      const body = await request<{ data: PublicTenant }>(
        'get',
        '/api/v1/public/tenant',
        undefined,
        request_options,
      );
      return body.data;
    },
    get: (path, request_options) => request('get', path, undefined, request_options),
    post: (path, body, request_options) => request('post', path, body, request_options),
    put: (path, body, request_options) => request('put', path, body, request_options),
    patch: (path, body, request_options) => request('patch', path, body, request_options),
    delete: (path, request_options) => request('delete', path, undefined, request_options),
  } as ApiClient;
}

/** `?a=1&b=two`, or an empty string. Null and undefined values are omitted. */
function queryString(query: Record<string, QueryValue> | undefined): string {
  if (query === undefined) return '';
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null) continue;
    params.append(key, String(value));
  }
  const serialized = params.toString();
  return serialized === '' ? '' : `?${serialized}`;
}

/**
 * The body, or `undefined` when there is not one to read.
 *
 * A 500 from a proxy is HTML, and an empty 202 has no body at all; neither may turn
 * into a parse exception that hides the status code the caller actually needs.
 */
async function readJson(response: Response): Promise<unknown> {
  try {
    const text = await response.text();
    if (text === '') return undefined;
    return JSON.parse(text) as unknown;
  } catch {
    return undefined;
  }
}
